import React, { useEffect, useMemo, useState } from 'react';
import { useQuery } from '@tanstack/react-query';
import {
  Alert,
  Autocomplete,
  Box,
  Button,
  Chip,
  Collapse,
  CircularProgress,
  Dialog,
  DialogContent,
  DialogTitle,
  IconButton,
  MenuItem,
  Stack,
  TextField,
  Typography,
  useMediaQuery,
  useTheme,
  Tab,
  Tabs,
} from '@mui/material';
import { Add, Close, DeleteOutline, DragIndicator, ExpandMore, ReceiptLong } from '@mui/icons-material';
import {
  ParlayLeg,
  getLatestDataInfo,
  priceSlip,
  ProbUncertaintyBand,
  SimDriverResult,
  SimModel,
  SlipOutcome,
  SimulationRun,
  SimulationSlip,
} from '../services/api';
import { bandFor, evaluateBet, growthBps, marketForTopN } from '../utils/betValue';
import { getModalBodyBg } from './DriverDetailsModal';
import { impliedProb, legLabel, parseAmericanOdds, shortLegLabel } from '../utils/parlay';
import {
  americanForProb,
  betProbs,
  hitCorrelation,
  histogramEdges,
  likelyOutcomes,
  balancedStakes,
  biggestExposure,
  exposureGroups,
  maxValueStakes,
  netForMask,
  PricedBet,
  profitHistogram,
  slipStats,
  SlipStats,
  dollars,
  money,
  pct,
  remapMasks,
  steadierStakes,
} from '../utils/betSlip';
import { useChartTokens, useWidth } from './charts/chartKit';
import { closestCenter, DndContext, DragEndEvent, PointerSensor, useSensor, useSensors } from '@dnd-kit/core';
import { arrayMove, SortableContext, useSortable, verticalListSortingStrategy } from '@dnd-kit/sortable';
import { CSS } from '@dnd-kit/utilities';
import {
  BetDraft,
  BetType,
  draftFromLeg,
  emptyBet,
  isFilled,
  LegDraft,
  LegType,
  legsFor,
  readChecks,
  readSlip,
  StoredCheck,
  StoredChecks,
  syncSlip,
  useSlipPulled,
  useSlipSyncStatus,
  writeChecks,
  writeSlip,
} from '../utils/slipStore';
import { SegmentedToggle } from './SegmentedToggle';
import { EXPLORE_TABS, ExploreTab, ProfitSides, SlipExplore, Swatch, useModelColors } from './SlipExplore';
import { SlipLiveNote } from './SlipLiveNote';
import { BookDot, BookSelect } from './BookSelect';

type SuggestionKey = 'yours' | 'value' | 'balanced' | 'steady';
const TYPE_LABEL: Record<BetType, string> = {
  win: 'Win',
  top3: 'Top 3',
  top5: 'Top 5',
  top10: 'Top 10',
  top20: 'Top 20',
  h2h: 'Head to head',
  group: 'Best of group',
  parlay: 'Parlay',
};
const N_SIMS = 20000;

const betLabel = (legs: ParlayLeg[], anyLeg = false) =>
  legs.length === 1
    ? legLabel(legs[0])
    : anyLeg
      ? `Any of: ${legs.map(shortLegLabel).join(' / ')}`
      : `Parlay: ${legs.map(shortLegLabel).join(' + ')}`;
/** A parlay that wins when any of its legs hits. */
const isAny = (b: BetDraft | undefined) => !!b && b.type === 'parlay' && !!b.anyLeg;

const fmtOdds = (o: number | null) => (o == null ? '—' : o > 0 ? `+${o}` : `${o}`);

/** A leg's own probability from the run on screen (for a parlay's uncertainty band). */
const legProb = (leg: ParlayLeg, drivers: SimDriverResult[]): number => {
  const d = drivers.find((x) => x.driver === leg.driver);
  if (!d || leg.kind !== 'top_n') return 0.5;
  const n = leg.n ?? 10;
  if (n === 1) return d.winProb;
  if (n === 3) return d.top3Prob;
  if (n === 5) return d.top5Prob;
  if (n === 10) return d.top10Prob;
  if (n === 20) return d.top20Prob;
  return d.finishDist ? d.finishDist.slice(0, n).reduce((t, v) => t + v, 0) : 0.5;
};

/** Uncertainty in points for a bet at probability p: the market's backtest band, plus the simulation's own error. */
const betBandPts = (
  legs: ParlayLeg[],
  p: number,
  drivers: SimDriverResult[],
  bands: ProbUncertaintyBand[] | undefined,
  nSims: number,
  anyLeg = false,
): number => {
  const mc = Math.sqrt((p * (1 - p)) / nSims) * 100;
  const legBand = (leg: ParlayLeg, q: number) =>
    bandFor(bands, leg.kind === 'top_n' ? marketForTopN(leg.n ?? 10) : 'h2h', q);
  if (legs.length === 1) return Math.sqrt(legBand(legs[0], p) ** 2 + mc ** 2);
  if (anyLeg) {
    // Any leg hits = 1 - no leg hits: each leg's band applied to its miss probability, combined as for a parlay of
    // misses (as the parlay builder's "Any" does). The band in points is the same for P(any) and P(none).
    let missSq = 0;
    legs.forEach((l) => {
      const q = legProb(l, drivers);
      if (q < 1) missSq += (legBand(l, q) / 100 / (1 - q)) ** 2;
    });
    return Math.sqrt(((1 - p) * Math.sqrt(missSq) * 100) ** 2 + mc ** 2);
  }
  // Parlay: each leg's relative uncertainty combined, scaled to the joint probability (as the parlay builder does).
  let relSq = 0;
  legs.forEach((l) => {
    const q = legProb(l, drivers);
    if (q > 0) relSq += (legBand(l, q) / 100 / q) ** 2;
  });
  return Math.sqrt((p * Math.sqrt(relSq) * 100) ** 2 + mc ** 2);
};

interface Props {
  open: boolean;
  onClose: () => void;
  run: SimulationRun;
  /** The current model's results (for driver lists and parlay leg probabilities). */
  drivers: SimDriverResult[];
  /** Track Specialists' results, when loaded (parlay leg probabilities under that model). */
  layeredDrivers?: SimDriverResult[];
  model: SimModel;
  uncertainty?: ProbUncertaintyBand[];
  /** The parlay builder's legs, offered as a one-click bet. */
  parlayLegs: ParlayLeg[];
}

/** Check a whole bet slip against the simulation: how it plays out, which bets are worth it, better ways to stake it. */
export const BetSlipDialog: React.FC<Props> = ({
  open,
  onClose,
  run,
  drivers,
  layeredDrivers,
  model,
  uncertainty,
  parlayLegs,
}) => {
  const theme = useTheme();
  const bodyBg = getModalBodyBg(theme);
  const isMobile = useMediaQuery(theme.breakpoints.down('sm'));
  const names = useMemo(
    () => [...drivers].sort((a, b) => a.projectedRank - b.projectedRank).map((d) => d.driver),
    [drivers],
  );

  // The slip is remembered per track in this browser (a convenience; never needed for correctness).
  const [bets, setBets] = useState<BetDraft[]>(() => {
    const saved = readSlip(run.site);
    return saved.length ? saved : [emptyBet()];
  });
  useEffect(() => writeSlip(run.site, bets), [bets, run.site]);

  // The model(s) the Slip Builder shows: starts on the page's model; "both" compares Base and Track Specialists.
  const [mode, setMode] = useState<SimModel | 'both'>(model);
  const shownModels: SimModel[] = mode === 'both' ? ['base', 'layered'] : [mode];
  // Each model's last check. `stakes` are the stakes at check time: suggestions are always built from the slip you
  // checked, so applying one lets you switch between them instead of generating new suggestions from it. Kept with
  // the slip (in this browser and, signed in, the account), so reopening shows the results without pricing again.
  const [checks, setChecks] = useState<StoredChecks>(() => readChecks(run.site));
  useEffect(() => writeChecks(run.site, checks), [checks, run.site]);
  // The check behind the single-model views (the model shown, or Base when comparing).
  const result: StoredCheck | null = checks[mode === 'both' ? 'base' : mode] ?? null;

  // Signed in: bring the slip in line with the account when the Slip Builder opens (load a newer copy saved on
  // another device, or save this browser's changes). Later edits save on their own a couple of seconds after.
  const syncStatus = useSlipSyncStatus();
  const [fromOtherDevice, setFromOtherDevice] = useState(false);
  useEffect(() => {
    void syncSlip(run.site);
  }, [run.site]);
  useSlipPulled(run.site, (other) => {
    const saved = readSlip(run.site);
    setBets(saved.length ? saved : [emptyBet()]);
    setChecks(readChecks(run.site));
    setFromOtherDevice(other);
  });
  const [showErrors, setShowErrors] = useState(false);
  // Drag a bet by its handle to reorder the slip. A checked result is renumbered to match, so it stays current.
  const sensors = useSensors(useSensor(PointerSensor, { activationConstraint: { distance: 4 } }));
  const onDragEnd = ({ active, over }: DragEndEvent) => {
    if (!over || active.id === over.id) return;
    const from = bets.findIndex((b) => b.id === active.id);
    const to = bets.findIndex((b) => b.id === over.id);
    if (from < 0 || to < 0) return;
    const next = arrayMove(bets, from, to);
    setBets(next);
    const renumber = (r: StoredCheck | undefined): StoredCheck | undefined => {
      if (!r) return r;
      const pos = r.ids.map((id) => next.findIndex((b) => b.id === id));
      if (pos.some((p) => p < 0) || pos.length !== next.length) return r; // bets changed too: needs a new check anyway
      const place = <T,>(xs: T[]) => {
        const out = new Array<T>(xs.length);
        xs.forEach((x, i) => (out[pos[i]] = x));
        return out;
      };
      return {
        ...r,
        slip: { ...r.slip, outcomes: remapMasks(r.slip.outcomes, pos), missing: place(r.slip.missing) },
        keys: place(r.keys),
        ids: place(r.ids),
        stakes: place(r.stakes),
      };
    };
    setChecks((c) => ({ base: renumber(c.base), layered: renumber(c.layered) }));
  };
  // The bet editor collapses to a one-line summary after a check, so the results aren't hidden below a long slip.
  // It starts folded when a stored check still matches the slip, so reopening goes straight to the results.
  const [editorOpen, setEditorOpen] = useState(
    () => !shownModels.every((m) => checks[m]?.ids.join() === bets.map((b) => b.id).join()),
  );
  const contentRef = React.useRef<HTMLDivElement>(null);
  const [checking, setChecking] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const [chartView, setChartView] = useState<'bars' | 'curve'>('bars');
  // Results tabs: the overview, then the exploring views (what if, each bet, stakes, risk, goals).
  const [view, setView] = useState<'overview' | ExploreTab>('overview');

  const update = (id: string, patch: Partial<BetDraft>) =>
    setBets((prev) => prev.map((b) => (b.id === id ? { ...b, ...patch } : b)));
  // New bets start at the book of the latest bet that has one (most slips are built one book at a time).
  const lastBook = [...bets].reverse().find((b) => b.book)?.book;
  // Bets and stake per book, for the "By book" row (only once some bet has a book).
  const byBook = bets.some((b) => b.book)
    ? bets.filter(isFilled).reduce((m, b) => {
        const k = b.book ?? '';
        const g = m.get(k) ?? { n: 0, stake: 0 };
        m.set(k, { n: g.n + 1, stake: g.stake + Math.max(0, Number(b.stakeText) || 0) });
        return m;
      }, new Map<string, { n: number; stake: number }>())
    : null;
  const remove = (id: string) => setBets((prev) => (prev.length > 1 ? prev.filter((b) => b.id !== id) : [emptyBet()]));

  const legsByBet = bets.map(legsFor);
  const keys = legsByBet.map((l, i) => JSON.stringify(l) + (isAny(bets[i]) ? '|any' : ''));
  const ready = bets.map((b, i) => !!legsByBet[i] && parseAmericanOdds(b.oddsText) != null);
  // A model's check is current when it was made with exactly these bets, in this order.
  const freshFor = (m: SimModel) => {
    const c = checks[m];
    return !!c && c.ids.join() === bets.map((b) => b.id).join() && c.keys.join('|') === keys.join('|');
  };
  const fresh = shownModels.every(freshFor);
  const anyCheck = !!(checks.base || checks.layered);
  const anyFresh = freshFor('base') || freshFor('layered');
  // A check can be out of date even when the bets match: newer race data or other tracks left out.
  const sameTracks = (a: string[], b: string[]) => [...a].sort().join('|') === [...b].sort().join('|');
  // The latest race loaded (cached an hour). A check's ratings use races before the day it was run (its asOf), so a
  // race on or after that day means the check doesn't include it.
  const { data: latest } = useQuery({
    queryKey: ['latestDataInfo'],
    queryFn: getLatestDataInfo,
    staleTime: 60 * 60 * 1000,
  });
  const chaosName = (c?: string) => (c === 'wild' ? 'Wild' : c === 'calm' ? 'Calm' : 'Normal');
  const fmtDay = (d: string) =>
    new Date(`${d}T12:00:00`).toLocaleDateString(undefined, { month: 'short', day: 'numeric' });
  const outdatedFor = (c: StoredCheck | undefined): string | null =>
    !c
      ? null
      : (c.chaos ?? 'normal') !== (run.chaos ?? 'normal')
        ? `Checked for a ${chaosName(c.chaos)} race; this run is a ${chaosName(run.chaos)} race.`
        : latest?.latest_date && String(latest.latest_date).slice(0, 10) >= c.slip.asOf
          ? `Checked on ${fmtDay(c.slip.asOf)}, before the ${latest.track} race on ${fmtDay(String(latest.latest_date).slice(0, 10))} was added.`
          : !sameTracks(c.slip.excludedTracks ?? [], run.excludedTracks ?? [])
            ? 'Checked with a different set of tracks left out of the simulation.'
            : null;
  const outdated = shownModels.map((m) => (freshFor(m) ? outdatedFor(checks[m]) : null)).find(Boolean) ?? null;

  const check = async (models: SimModel[] = shownModels) => {
    const legs = legsByBet.filter(Boolean) as ParlayLeg[][];
    if (legs.length !== bets.length || bets.some((b) => parseAmericanOdds(b.oddsText) == null)) {
      setShowErrors(true);
      setEditorOpen(true);
      setError(
        'Fill in the highlighted fields (driver, the other driver for head to heads, and odds) before checking.',
      );
      return;
    }
    setShowErrors(false);
    setChecking(true);
    setError(null);
    try {
      // "Any leg hits" parlays are priced as such by the pricer (one bet each, however many legs).
      const slipBets = legs.map((l, i) => ({ legs: l, ...(isAny(bets[i]) && { any: true }) }));
      const slips = await Promise.all(
        models.map((m) => priceSlip(run.site, slipBets, N_SIMS, run.excludedTracks, m, run.chaos)),
      );
      const stakes = bets.map((b) => Math.max(0, Number(b.stakeText) || 0));
      const ids = bets.map((b) => b.id);
      setChecks((c) => {
        const next = { ...c };
        models.forEach(
          (m, i) => (next[m] = { slip: slips[i], keys, ids, stakes, model: m, ...(run.chaos && { chaos: run.chaos }) }),
        );
        return next;
      });
      setEditorOpen(false);
      contentRef.current?.scrollTo({ top: 0, behavior: 'smooth' });
    } catch (e) {
      setError(e instanceof Error ? e.message : 'Failed to check the bet slip');
    } finally {
      setChecking(false);
    }
  };

  // Switching models checks the slip under the newly shown model(s) the first time (when it's been checked before).
  const switchMode = (m: SimModel | 'both') => {
    setMode(m);
    const want: SimModel[] = m === 'both' ? ['base', 'layered'] : [m];
    const missing = want.filter((x) => !freshFor(x));
    if (anyFresh && missing.length && !checking) void check(missing);
  };

  const analyze = (result: StoredCheck | undefined, m: SimModel): Analysis | null => {
    if (!result || !freshFor(m)) return null;
    const modelDrivers = m === 'layered' ? (layeredDrivers ?? drivers) : model === 'base' ? drivers : run.drivers;
    const { outcomes } = result.slip;
    const priced: PricedBet[] = bets.map((b) => ({
      odds: parseAmericanOdds(b.oddsText) ?? 100,
      stake: parseAmericanOdds(b.oddsText) != null ? Math.max(0, Number(b.stakeText) || 0) : 0,
    }));
    const probs = betProbs(outcomes, bets.length);
    const evals = bets.map((b, i) => {
      const odds = parseAmericanOdds(b.oddsText);
      if (odds == null) return null;
      const band = betBandPts(legsByBet[i]!, probs[i], modelDrivers, uncertainty, result.slip.nSims, isAny(bets[i]));
      return { ...evaluateBet(probs[i], odds, band, 0.25), band };
    });
    const value = evals.map((e) => !!e && (e.confidence === 'clear' || e.confidence === 'marginal'));
    const budget = priced.reduce((t, b) => t + b.stake, 0);
    const cur = slipStats(outcomes, priced);
    const withStakes = (st: number[]) => priced.map((b, i) => ({ ...b, stake: st[i] }));
    // The slip as checked (its stakes, today's odds): the fixed starting point for every suggestion.
    const checkedStakes = result.stakes.map((v, i) => (parseAmericanOdds(bets[i].oddsText) != null ? v : 0));
    const checkedBudget = checkedStakes.reduce((t, v) => t + v, 0);
    const checkedStats = slipStats(outcomes, withStakes(checkedStakes));
    // Max value needs bets that clear our margin. Balanced and Steadier can also use bets that are too close to call
    // (never overpriced ones): a likely bet with a near-zero edge is what steadies a slip. Both must expect at least
    // as much as the slip you checked; if that slip expects a loss, a smaller expected loss still counts.
    const usable = evals.map((e, i) => value[i] || verdictOf(e) === 'close');
    let floor = checkedStats.ev;
    // Exposure groups: bets that win and lose together (same backed driver, or strongly correlated hits) share one
    // 40% limit, so a suggestion can't pile the money onto one driver through several markets.
    const backed = legsByBet.map((legs) => new Set((legs ?? []).map((l) => l.driver)));
    const groups = exposureGroups(outcomes, bets.length, (a, b) => Array.from(backed[a]).some((d) => backed[b].has(d)));
    // A stacked slip can expect more than any slip within the caps can, which would leave Balanced and Steadier
    // nothing to offer. So their floor is your slip's expected profit, or, when that's out of reach under the caps,
    // 90% of the most any capped slip of these bets can expect (the max-value arrangement over the same bets).
    if (checkedBudget > 0 && usable.some(Boolean)) {
      const bestCapped = slipStats(
        outcomes,
        withStakes(maxValueStakes(outcomes, priced, usable, checkedBudget, 0.4, groups)),
      ).ev;
      // Only the max-value arrangement itself reaches the capped maximum, so when that's the binding floor, leave a
      // little room below it (10%) for Balanced and Steadier to trade expected profit for a steadier slip.
      if (floor >= bestCapped) floor = bestCapped - 0.1 * Math.abs(bestCapped);
    }
    const candidates: { key: SuggestionKey; title: string; blurb: string; stakes: number[] }[] = [];
    if (checkedBudget > 0 && value.some(Boolean))
      candidates.push({
        key: 'value',
        title: 'Max value',
        blurb:
          'Most value from the bets that clear our margin (edge weighed against how often each hits, as the board scanner ranks them), with no more than 40% of the money on bets that win and lose together.',
        // Ranked like the board scanner: bankroll growth on our probability minus its margin, so a thin edge on a long
        // price doesn't outrank a solid edge on a likely bet. The group caps still apply.
        stakes: maxValueStakes(
          outcomes,
          priced,
          value,
          checkedBudget,
          0.4,
          groups,
          priced.map((b, i) => (evals[i] ? growthBps(probs[i], b.odds, evals[i]!.band) : 0)),
        ),
      });
    if (checkedBudget > 0 && usable.some(Boolean))
      candidates.push(
        {
          key: 'balanced',
          title: 'Balanced',
          blurb:
            'Best typical result (what you most often walk away with), expecting at least as much as your slip (or close to the most a spread-out slip can).',
          stakes: balancedStakes(outcomes, priced, usable, checkedBudget, floor, 0.4, groups),
        },
        {
          key: 'steady',
          title: 'Steadier',
          blurb:
            'Best chance of finishing ahead, expecting at least as much as your slip (or close to the most a spread-out slip can).',
          stakes: steadierStakes(outcomes, priced, usable, checkedBudget, floor, 0.4, groups),
        },
      );
    // Drop empty suggestions, ones identical to your slip, and duplicates (the first with the same stakes wins).
    const seen = new Set<string>([checkedStakes.join()]);
    // The biggest single exposure in a slip, in words: "64% rides on Kyle Larson (3 bets)".
    const exposureNote = (st: number[]): string | null => {
      // Measured against the money you set out to bet (the checked slip's total), like the 40% cap, so a suggestion
      // that leaves money unbet isn't flagged for what it does bet.
      const top = biggestExposure(st, groups);
      if (!top || checkedBudget <= 0 || top.amount / checkedBudget < 0.4) return null;
      const members = st.map((v, i) => i).filter((i) => st[i] > 0 && groups[i] === top.group);
      const common = Array.from(backed[members[0]] ?? []).filter((d) => members.every((i) => backed[i].has(d)));
      const what =
        members.length === 1
          ? betLabel(legsByBet[members[0]] ?? [], isAny(bets[members[0]]))
          : common.length === 1
            ? `${common[0]} (${members.length} bets)`
            : `${members.length} bets that win and lose together`;
      return `${pct(top.amount / checkedBudget)} rides on ${what}`;
    };
    const describe = (st: number[]) => ({
      stats: slipStats(outcomes, withStakes(st)),
      likely: likelyOutcomes(outcomes, 3, withStakes(st)),
      exposure: exposureNote(st),
    });
    const options = candidates
      .filter((o) => o.stakes.some((v) => v > 0) && !seen.has(o.stakes.join()) && !!seen.add(o.stakes.join()))
      .map((o) => ({ ...o, ...describe(o.stakes) }));
    // Why there are no suggestions, in plain words.
    const noSuggestions =
      options.length > 0 || checkedBudget <= 0
        ? null
        : !usable.some(Boolean)
          ? 'No suggestions: every bet on this slip is overpriced at these odds.'
          : "No suggestions: there's no other way to stake these bets that expects at least as much as your slip.";
    const yours = {
      key: 'yours' as const,
      title: 'Your slip',
      blurb: 'The stakes you checked.',
      stakes: checkedStakes,
      ...describe(checkedStakes),
    };
    const pairs: { a: number; b: number; r: number }[] = [];
    for (let a = 0; a < bets.length; a++)
      for (let b = a + 1; b < bets.length; b++) {
        if (!priced[a].stake || !priced[b].stake) continue;
        const r = hitCorrelation(outcomes, a, b);
        if (Math.abs(r) >= 0.25) pairs.push({ a, b, r });
      }
    return {
      priced,
      probs,
      evals,
      value,
      budget,
      cur,
      options,
      yours,
      noSuggestions,
      checkedBudget,
      withStakes,
      pairs,
      likely: likelyOutcomes(outcomes, 4, priced),
      groups,
      usable,
      outcomes,
    };
  };
  const analyses = useMemo(
    () => ({ base: analyze(checks.base, 'base'), layered: analyze(checks.layered, 'layered') }),
    // eslint-disable-next-line react-hooks/exhaustive-deps
    [checks, bets, drivers, layeredDrivers, uncertainty],
  );
  // The single-model views use the model shown; comparing needs both.
  const analysis = mode === 'both' ? (analyses.base && analyses.layered ? analyses.base : null) : analyses[mode];

  // Your checked slip and the suggestions; the outlined card is the slip shown above.
  const cards = analysis ? [analysis.yours, ...analysis.options] : [];
  const currentKey = analysis ? analysis.priced.map((b) => b.stake).join() : '';
  const viewing = cards.find((o) => o.stakes.join() === currentKey) ?? null;

  const applyStakes = (stakes: number[]) =>
    setBets((prev) => prev.map((b, i) => ({ ...b, stakeText: String(stakes[i] ?? 0) })));

  const labels = bets.map((b, i) => (legsByBet[i] ? betLabel(legsByBet[i]!, isAny(b)) : 'Unfinished bet'));

  return (
    <Dialog
      open={open}
      onClose={onClose}
      maxWidth="lg"
      fullWidth
      fullScreen={isMobile}
      // Once there are results, a steady height on desktop so switching tabs doesn't make the dialog jump.
      PaperProps={{
        sx: {
          // A bit wider than a medium dialog, so the bet tables and cards don't wrap and misalign.
          maxWidth: 1100,
          borderRadius: 2,
          backgroundImage: 'none',
          border: 1,
          borderColor: 'divider',
          ...(analysis && !isMobile && { height: 'calc(100% - 64px)' }),
        },
      }}
    >
      <DialogTitle sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
        <ReceiptLong color="primary" />
        <Box sx={{ flex: 1, minWidth: 0 }}>
          {/* On narrow screens the model switch wraps onto its own line instead of pushing out the close button. */}
          <Stack direction="row" columnGap={1} rowGap={0.75} alignItems="center" flexWrap="wrap" useFlexGap>
            <Box component="span" sx={{ whiteSpace: 'nowrap' }}>
              Slip Builder
            </Box>
            {/* Which model judges the slip; Both compares them. Each model keeps its own last check. */}
            <SegmentedToggle
              aria-label="Model"
              size="small"
              value={mode}
              onChange={(v: string) => switchMode(v as SimModel | 'both')}
              options={[
                { value: 'base', label: 'Base' },
                { value: 'layered', label: isMobile ? 'Specialists' : 'Track Specialists' },
                { value: 'both', label: 'Both' },
              ]}
            />
            {syncStatus !== 'off' && (
              <Typography
                variant="caption"
                color={syncStatus === 'error' ? 'warning.main' : 'text.secondary'}
                title={
                  syncStatus === 'error'
                    ? "Couldn't save to your account; it's kept in this browser and will retry on your next change"
                    : 'Saved to your account, so it follows you to other devices'
                }
                sx={{ whiteSpace: 'nowrap' }}
              >
                {syncStatus === 'saving' ? 'Saving…' : syncStatus === 'error' ? 'Not saved' : 'Saved'}
              </Typography>
            )}
          </Stack>
          <Typography variant="body2" color="text.secondary">
            {run.site} · every bet judged on the same {N_SIMS.toLocaleString()} simulated races
            {mode === 'both' ? ' · under both models' : ''}
          </Typography>
        </Box>
        <IconButton onClick={onClose} aria-label="Close">
          <Close />
        </IconButton>
      </DialogTitle>
      <DialogContent dividers ref={contentRef} sx={{ bgcolor: bodyBg }}>
        <SlipLiveNote site={run.site} onOpen={onClose} />
        {/* Collapsed after a check: a one-line summary of the slip, with Edit to reopen the editor. */}
        {!editorOpen && (
          <Stack
            direction="row"
            alignItems="center"
            spacing={1}
            sx={{ p: 1.5, border: 1, borderColor: 'divider', borderRadius: 1.5 }}
          >
            <Typography
              variant="body2"
              sx={{ flex: 1, minWidth: 0 }}
              noWrap
              title={labels.filter((_, i) => legsByBet[i]).join(' · ')}
            >
              <b>
                {bets.length} bet{bets.length === 1 ? '' : 's'}
              </b>{' '}
              · total $
              {bets
                .reduce((t, b) => t + Math.max(0, Number(b.stakeText) || 0), 0)
                .toLocaleString(undefined, { minimumFractionDigits: 2, maximumFractionDigits: 2 })}
              <Typography component="span" variant="body2" color="text.secondary">
                {' '}
                · {labels.filter((_, i) => legsByBet[i]).join(' · ')}
              </Typography>
            </Typography>
            <Button size="small" variant="outlined" endIcon={<ExpandMore />} onClick={() => setEditorOpen(true)}>
              Edit slip
            </Button>
          </Stack>
        )}
        <Collapse in={editorOpen}>
          {/* Bet editor */}
          <DndContext sensors={sensors} collisionDetection={closestCenter} onDragEnd={onDragEnd}>
            <SortableContext items={bets.map((b) => b.id)} strategy={verticalListSortingStrategy}>
              <Stack spacing={1.5}>
                {bets.map((b) => (
                  <SortableBet key={b.id} id={b.id} canDrag={bets.length > 1}>
                    {(handle) => (
                      <BetEditor
                        bet={b}
                        names={names}
                        showErrors={showErrors}
                        onChange={(patch) => update(b.id, patch)}
                        onRemove={() => remove(b.id)}
                        dragHandle={handle}
                      />
                    )}
                  </SortableBet>
                ))}
              </Stack>
            </SortableContext>
          </DndContext>
          <Stack direction="row" spacing={1} flexWrap="wrap" useFlexGap sx={{ mt: 1.5 }} alignItems="center">
            <Button
              size="small"
              variant="outlined"
              startIcon={<Add />}
              onClick={() => setBets((p) => [...p, { ...emptyBet(), book: lastBook }])}
              disabled={bets.length >= 12}
            >
              Add bet
            </Button>
            {parlayLegs.length > 0 && (
              <Button
                size="small"
                variant="outlined"
                onClick={() =>
                  setBets((p) => [
                    ...p.filter((x) => legsFor(x) || x.oddsText || x.stakeText),
                    parlayLegs.length === 1
                      ? { ...emptyBet(), book: lastBook, ...draftFromLeg(parlayLegs[0]) }
                      : { ...emptyBet(), book: lastBook, type: 'parlay', legs: parlayLegs.map(draftFromLeg) },
                  ])
                }
              >
                Add my parlay ({parlayLegs.length} leg{parlayLegs.length === 1 ? '' : 's'})
              </Button>
            )}
            <Box sx={{ flex: 1 }} />
            <Typography variant="body2" color="text.secondary">
              Total $
              {bets
                .reduce((t, b) => t + Math.max(0, Number(b.stakeText) || 0), 0)
                .toLocaleString(undefined, { minimumFractionDigits: 2, maximumFractionDigits: 2 })}
            </Typography>
            {result && (
              <Button size="small" onClick={() => setEditorOpen(false)}>
                Hide
              </Button>
            )}
            <Button variant="contained" onClick={() => void check()} disabled={checking || !ready.some(Boolean)}>
              {checking ? <CircularProgress size={20} /> : fresh ? 'Check again' : 'Run check'}
            </Button>
          </Stack>
          {byBook && byBook.size > 0 && (
            <Stack
              direction="row"
              alignItems="center"
              spacing={1}
              useFlexGap
              flexWrap="wrap"
              sx={{ mt: 1.5, pt: 1.5, borderTop: 1, borderColor: 'divider', rowGap: 1 }}
            >
              <Typography sx={{ fontSize: 11, fontWeight: 700, letterSpacing: '0.06em', color: 'text.secondary', mr: 0.5 }}>
                BY BOOK
              </Typography>
              {Array.from(byBook).map(([k, g]) => (
                <Stack
                  key={k || 'none'}
                  direction="row"
                  spacing={0.75}
                  alignItems="center"
                  sx={{ border: 1, borderColor: 'divider', borderRadius: 999, px: 1.25, py: 0.4, fontSize: 13 }}
                >
                  <BookDot book={k || undefined} />
                  <Box component="span" sx={{ fontWeight: 600 }}>
                    {k || 'No book'}
                  </Box>
                  <Box component="span" sx={{ color: 'text.secondary' }}>
                    {g.n} bet{g.n === 1 ? '' : 's'} · $
                    {g.stake.toLocaleString(undefined, { maximumFractionDigits: 2 })}
                  </Box>
                </Stack>
              ))}
            </Stack>
          )}
        </Collapse>
        {error && (
          <Alert severity="error" sx={{ mt: 2 }}>
            {error}
          </Alert>
        )}
        {fromOtherDevice && (
          <Alert severity="info" sx={{ mt: 2 }} onClose={() => setFromOtherDevice(false)}>
            Updated with the copy of this slip saved from another device.
          </Alert>
        )}
        {fresh && outdated && !checking && (
          <Alert
            severity="info"
            sx={{ mt: 2 }}
            action={
              <Button color="inherit" size="small" onClick={() => void check()}>
                Check again
              </Button>
            }
          >
            {outdated} These are the earlier results; check again for the latest.
          </Alert>
        )}
        {anyCheck && !anyFresh && !checking && (
          <Alert severity="info" sx={{ mt: 2 }}>
            The bets changed since the last check. Check again to update (changing only odds or stakes doesn't need it).
          </Alert>
        )}
        {anyFresh && !fresh && (
          <Alert
            severity="info"
            sx={{ mt: 2 }}
            action={
              !checking && (
                <Button
                  color="inherit"
                  size="small"
                  onClick={() => void check(shownModels.filter((m) => !freshFor(m)))}
                >
                  Check
                </Button>
              )
            }
          >
            {checking
              ? `Checking with ${mode === 'both' ? 'both models' : mode === 'layered' ? 'Track Specialists' : 'the Base model'}…`
              : `This slip hasn't been checked with ${shownModels
                  .filter((m) => !freshFor(m))
                  .map((m) => (m === 'layered' ? 'Track Specialists' : 'the Base model'))
                  .join(' or ')} yet.`}
          </Alert>
        )}

        {analysis && (
          <Box sx={{ mt: 2 }}>
            <Tabs
              value={view}
              onChange={(_, v) => setView(v)}
              variant="scrollable"
              allowScrollButtonsMobile
              sx={{ mb: 2, borderBottom: 1, borderColor: 'divider' }}
            >
              <Tab value="overview" label="Overview" />
              {EXPLORE_TABS.map((t) => (
                <Tab key={t.value} value={t.value} label={t.label} />
              ))}
            </Tabs>
            {view !== 'overview' && analysis.budget > 0 && (
              <SlipExplore
                // Remount when switching models, so each view starts fresh (e.g. the What if toggles).
                key={mode}
                tab={view}
                outcomes={analysis.outcomes}
                bets={analysis.priced}
                labels={labels}
                budget={analysis.checkedBudget}
                groups={analysis.groups}
                // Comparing, only bets usable under both models (never one either model calls overpriced).
                usable={
                  mode === 'both' ? analysis.usable.map((u, i) => u && !!analyses.layered?.usable[i]) : analysis.usable
                }
                compare={
                  mode === 'both' && analyses.layered
                    ? { base: analyses.base!.outcomes, layered: analyses.layered.outcomes }
                    : undefined
                }
                onApply={(st) => {
                  applyStakes(st);
                  setView('overview');
                }}
              />
            )}
            {view !== 'overview' && analysis.budget <= 0 && (
              <Typography variant="body2" color="text.secondary">
                Add stakes to explore the slip.
              </Typography>
            )}
            {view === 'overview' && mode === 'both' && analyses.base && analyses.layered && (
              <CompareOverview labels={labels} bets={bets} a={analyses.base} b={analyses.layered} />
            )}
            {view === 'overview' && mode !== 'both' && (
              <>
                <VerdictTable labels={labels} bets={bets} analysis={analysis} missing={checks[mode]!.slip.missing} />

                <Typography variant="h6" sx={{ mt: 3, mb: 1.5 }}>
                  How this slip plays out
                </Typography>
                {analysis.budget <= 0 ? (
                  <Typography variant="body2" color="text.secondary">
                    Add stakes to see how the slip plays out.
                  </Typography>
                ) : (
                  <>
                    <Stack direction="row" spacing={1.5} flexWrap="wrap" useFlexGap sx={{ mb: 2 }}>
                      <Tile
                        label="Expected profit"
                        value={money(analysis.cur.ev)}
                        sub={`on ${dollars(analysis.budget)} staked`}
                        strong
                      />
                      <Tile label="Chance of profit" value={pct(analysis.cur.pProfit)} sub="finish the race ahead" />
                      <Tile
                        label="Most likely range"
                        value={`${money(analysis.cur.p10)} to ${money(analysis.cur.p90)}`}
                        sub="8 races in 10"
                      />
                      <Tile
                        label="Lose everything"
                        value={pct(analysis.cur.pAllLose)}
                        sub={`worst ${money(analysis.cur.worst)}, best ${money(analysis.cur.best)}`}
                      />
                    </Stack>
                    <Stack direction="row" alignItems="center" spacing={1.5} sx={{ mb: 1 }} flexWrap="wrap" useFlexGap>
                      <Box sx={{ flex: 1 }} />
                      <SegmentedToggle
                        aria-label="Chart style"
                        size="small"
                        value={chartView}
                        onChange={setChartView}
                        options={[
                          { value: 'bars', label: 'Bars' },
                          { value: 'curve', label: 'Curve' },
                        ]}
                      />
                    </Stack>
                    <PayoutChart
                      view={chartView}
                      renderBin={(rows, lo, hi, share) => (
                        <>
                          <Typography variant="subtitle2">
                            {money(lo)} to {money(hi)} · {pct(share)} of races
                          </Typography>
                          <Typography variant="caption" color="text.secondary" sx={{ display: 'block', mb: 0.5 }}>
                            Every way the slip lands here: which bets hit, how often, and the exact profit.
                          </Typography>
                          {rows.slice(0, 8).map((r) => {
                            const hits = bets
                              .map((_, i) => i)
                              .filter((i) => r.mask & (1 << i) && analysis.priced[i].stake > 0);
                            return (
                              <Stack key={r.mask} direction="row" spacing={1.5} sx={{ py: 0.25 }}>
                                <Typography variant="body2" sx={{ width: 52, fontVariantNumeric: 'tabular-nums' }}>
                                  {r.prob < 0.01 ? '<1%' : pct(r.prob)}
                                </Typography>
                                <Typography variant="body2" sx={{ flex: 1 }}>
                                  {hits.length ? `${hits.map((i) => labels[i]).join(' + ')} hit` : 'Nothing hits'}
                                </Typography>
                                <Typography
                                  variant="body2"
                                  sx={{ fontWeight: 600, fontVariantNumeric: 'tabular-nums' }}
                                >
                                  {money(r.net)}
                                </Typography>
                              </Stack>
                            );
                          })}
                          {rows.length > 8 && (
                            <Typography variant="caption" color="text.secondary">
                              and {rows.length - 8} rarer combinations
                            </Typography>
                          )}
                        </>
                      )}
                      outcomes={analysis.outcomes}
                      base={analysis.priced}
                      baseStats={analysis.cur}
                    />
                    <Typography variant="caption" color="text.secondary">
                      {chartView === 'bars'
                        ? 'How often each profit or loss came up across the simulated races (click a bar to see what makes it up)'
                        : `A curve fitted to the slip's average, spread and lean. The darker part is where 8 races in 10 land (${money(analysis.cur.p10)} to ${money(analysis.cur.p90)}); the typical result is ${money(analysis.cur.p50)}`}{' '}
                      (orange = lose money, green = make money).
                    </Typography>

                    <Typography variant="subtitle2" sx={{ mt: 2, mb: 0.5 }}>
                      Most likely results of this slip (which bets hit)
                    </Typography>
                    {analysis.likely.map((o) => {
                      const hits = bets.map((_, i) => i).filter((i) => o.mask & (1 << i));
                      return (
                        <Stack key={o.mask} direction="row" spacing={1.5} sx={{ py: 0.25 }}>
                          <Typography variant="body2" sx={{ width: 44, fontVariantNumeric: 'tabular-nums' }}>
                            {pct(o.prob)}
                          </Typography>
                          <Typography variant="body2" sx={{ flex: 1 }}>
                            {hits.length ? `${hits.map((i) => labels[i]).join(' + ')} hit` : 'Nothing hits'}
                          </Typography>
                          <Typography variant="body2" sx={{ fontWeight: 600, fontVariantNumeric: 'tabular-nums' }}>
                            {money(o.net)}
                          </Typography>
                        </Stack>
                      );
                    })}

                    <Suggestions labels={labels} bets={bets} analysis={analysis} />

                    <Typography variant="subtitle2" sx={{ mt: 2, mb: 1 }}>
                      Better ways to stake the slip you checked (up to {dollars(analysis.checkedBudget)})
                    </Typography>
                    {analysis.noSuggestions ? (
                      <Typography variant="body2" color="text.secondary">
                        {analysis.noSuggestions}
                      </Typography>
                    ) : (
                      // One row of cards to scroll through sideways, snapping card by card.
                      <Box
                        sx={{
                          display: 'flex',
                          gap: 2,
                          overflowX: 'auto',
                          scrollSnapType: 'x mandatory',
                          pb: 1,
                          mx: -0.5,
                          px: 0.5,
                        }}
                      >
                        {cards.map((o) => (
                          <OptionCard
                            key={o.key}
                            title={o.title}
                            blurb={o.blurb}
                            labels={labels}
                            current={analysis.yours.stakes}
                            stakes={o.stakes}
                            stats={o.stats}
                            likely={o.likely}
                            exposure={o.exposure}
                            active={viewing?.key === o.key}
                            viewing={viewing?.key === o.key}
                            // Clicking anywhere on a card shows that slip above.
                            onSelect={() => applyStakes(o.stakes)}
                            onUse={() => applyStakes(o.stakes)}
                          />
                        ))}
                      </Box>
                    )}
                  </>
                )}
              </>
            )}
            <Typography variant="caption" color="text.secondary" sx={{ display: 'block', mt: 2 }}>
              Decision support from model estimates, not betting advice. Suggested slips never include overpriced bets.
            </Typography>
          </Box>
        )}
      </DialogContent>
    </Dialog>
  );
};

/** One bet: type, driver(s), odds and stake; parlays list their legs underneath. */
/** One bet in the editor that can be dragged by its handle to a new place in the slip. */
const SortableBet: React.FC<{
  id: string;
  canDrag: boolean;
  children: (handle: React.ReactNode) => React.ReactNode;
}> = ({ id, canDrag, children }) => {
  const { attributes, listeners, setNodeRef, setActivatorNodeRef, transform, transition, isDragging } = useSortable({
    id,
  });
  const handle = canDrag ? (
    <IconButton
      ref={setActivatorNodeRef}
      size="small"
      aria-label="Drag to reorder"
      {...attributes}
      {...listeners}
      sx={{
        mt: 0.5,
        ml: -0.75,
        cursor: isDragging ? 'grabbing' : 'grab',
        touchAction: 'none',
        color: 'text.secondary',
      }}
    >
      <DragIndicator fontSize="small" />
    </IconButton>
  ) : null;
  return (
    <Box
      ref={setNodeRef}
      style={{ transform: CSS.Translate.toString(transform), transition }}
      sx={{ position: 'relative', zIndex: isDragging ? 2 : undefined, opacity: isDragging ? 0.85 : 1 }}
    >
      {children(handle)}
    </Box>
  );
};

const BetEditor: React.FC<{
  bet: BetDraft;
  names: string[];
  /** After a failed check: mark every missing or invalid field in red. */
  showErrors: boolean;
  onChange: (patch: Partial<BetDraft>) => void;
  onRemove: () => void;
  /** The handle to drag this bet to a new place (absent when the slip has one bet). */
  dragHandle?: React.ReactNode;
}> = ({ bet, names, showErrors, onChange, onRemove, dragHandle }) => {
  const oddsBad = (bet.oddsText.trim() !== '' || showErrors) && parseAmericanOdds(bet.oddsText) == null;
  return (
    <Box sx={{ p: 1.5, border: 1, borderColor: 'divider', borderRadius: 1.5, bgcolor: 'background.paper' }}>
      <Stack direction="row" spacing={1} flexWrap="wrap" useFlexGap alignItems="flex-start">
        {dragHandle}
        <TextField
          select
          size="small"
          label="Bet"
          value={bet.type}
          onChange={(e) => {
            const type = e.target.value as BetType;
            onChange(
              type === 'parlay'
                ? {
                    type,
                    legs: bet.legs.length
                      ? bet.legs
                      : [
                          { type: 'top10', driver: bet.driver, others: [] },
                          { type: 'top10', driver: '', others: [] },
                        ],
                  }
                : { type, others: type === 'h2h' ? bet.others.slice(0, 1) : bet.others },
            );
          }}
          sx={{ width: 152 }}
        >
          {(Object.keys(TYPE_LABEL) as BetType[]).map((t) => (
            <MenuItem key={t} value={t}>
              {TYPE_LABEL[t]}
            </MenuItem>
          ))}
        </TextField>
        {bet.type !== 'parlay' && (
          <LegFields
            leg={bet}
            names={names}
            showErrors={showErrors}
            onChange={(p) => onChange(p as Partial<BetDraft>)}
          />
        )}
        {bet.type === 'parlay' && (
          <Box sx={{ alignSelf: 'center', flex: 1, minWidth: 160 }}>
            <SegmentedToggle
              aria-label="Parlay wins when"
              size="small"
              value={bet.anyLeg ? 'any' : 'all'}
              onChange={(v: string) => onChange({ anyLeg: v === 'any' })}
              options={[
                { value: 'all', label: 'Every leg hits' },
                { value: 'any', label: 'Any leg hits' },
              ]}
            />
          </Box>
        )}
        {/* Odds, stake and delete float right, so every bet's stake lines up whatever its driver fields are. */}
        <Stack direction="row" spacing={1} alignItems="flex-start" sx={{ ml: 'auto' }}>
          {/* The book sits beside the odds; phones give it a line of its own (below), where its name fits. */}
          <BookSelect value={bet.book} onChange={(book) => onChange({ book })} sx={{ display: { xs: 'none', sm: 'inline-flex' } }} />
          <TextField
            size="small"
            label="Odds"
            placeholder="+150"
            value={bet.oddsText}
            error={oddsBad}
            onChange={(e) => onChange({ oddsText: e.target.value })}
            sx={{ width: 88 }}
            inputProps={{ inputMode: 'text' }}
          />
          <TextField
            size="small"
            label="Stake $"
            value={bet.stakeText}
            onChange={(e) => onChange({ stakeText: e.target.value.replace(/[^0-9.]/g, '') })}
            sx={{ width: 88 }}
            inputProps={{ inputMode: 'decimal' }}
          />
          <IconButton aria-label="Remove bet" onClick={onRemove} sx={{ mt: 0.25 }}>
            <DeleteOutline />
          </IconButton>
        </Stack>
      </Stack>
      <BookSelect
        value={bet.book}
        onChange={(book) => onChange({ book })}
        width="100%"
        sx={{ display: { xs: 'inline-flex', sm: 'none' }, mt: 1.5 }}
      />
      {bet.type === 'parlay' && (
        <Stack spacing={1} sx={{ mt: 1, pl: { xs: 0, sm: 2 }, borderLeft: { sm: 2 }, borderColor: { sm: 'divider' } }}>
          {bet.legs.map((leg, i) => (
            <Stack key={i} direction="row" spacing={1} flexWrap="wrap" useFlexGap alignItems="flex-start">
              <TextField
                select
                size="small"
                label={`Leg ${i + 1}`}
                value={leg.type}
                onChange={(e) => {
                  const legs = [...bet.legs];
                  const type = e.target.value as LegType;
                  legs[i] = { ...leg, type, others: type === 'h2h' ? leg.others.slice(0, 1) : leg.others };
                  onChange({ legs });
                }}
                sx={{ width: 152 }}
              >
                {(Object.keys(TYPE_LABEL) as BetType[])
                  .filter((t) => t !== 'parlay')
                  .map((t) => (
                    <MenuItem key={t} value={t}>
                      {TYPE_LABEL[t]}
                    </MenuItem>
                  ))}
              </TextField>
              <LegFields
                leg={leg}
                names={names}
                showErrors={showErrors}
                onChange={(p) => {
                  const legs = [...bet.legs];
                  legs[i] = { ...leg, ...p };
                  onChange({ legs });
                }}
              />
              <IconButton
                aria-label="Remove leg"
                disabled={bet.legs.length <= 2}
                onClick={() => onChange({ legs: bet.legs.filter((_, k) => k !== i) })}
                sx={{ mt: 0.25 }}
              >
                <Close fontSize="small" />
              </IconButton>
            </Stack>
          ))}
          <Box>
            <Button
              size="small"
              startIcon={<Add />}
              disabled={bet.legs.length >= 8}
              onClick={() => onChange({ legs: [...bet.legs, { type: 'top10', driver: '', others: [] }] })}
            >
              Add leg
            </Button>
          </Box>
        </Stack>
      )}
    </Box>
  );
};

const LegFields: React.FC<{
  leg: { type: BetType; driver: string; others: string[] };
  names: string[];
  showErrors: boolean;
  onChange: (patch: Partial<LegDraft>) => void;
}> = ({ leg, names, showErrors, onChange }) => (
  <>
    <Autocomplete
      size="small"
      options={names}
      value={leg.driver || null}
      onChange={(_, v) => onChange({ driver: v ?? '' })}
      renderInput={(p) => (
        <TextField {...p} label="Driver" error={showErrors && !leg.driver} title={leg.driver || undefined} />
      )}
      sx={{ width: 210 }}
    />
    {leg.type === 'h2h' && (
      <Autocomplete
        size="small"
        options={names.filter((n) => n !== leg.driver)}
        value={leg.others[0] ?? null}
        onChange={(_, v) => onChange({ others: v ? [v] : [] })}
        renderInput={(p) => (
          <TextField
            {...p}
            label="Finishes ahead of"
            error={showErrors && leg.others.length !== 1}
            title={leg.others[0] || undefined}
          />
        )}
        sx={{ width: 210 }}
      />
    )}
    {leg.type === 'group' && (
      <Autocomplete
        multiple
        size="small"
        options={names.filter((n) => n !== leg.driver)}
        value={leg.others}
        onChange={(_, v) => onChange({ others: v.slice(0, 9) })}
        renderInput={(p) => (
          <TextField {...p} label="Best of the group vs" error={showErrors && leg.others.length === 0} />
        )}
        sx={{ minWidth: 190, flex: 1 }}
      />
    )}
  </>
);

type Analysis = {
  priced: PricedBet[];
  probs: number[];
  evals: ((ReturnType<typeof evaluateBet> & { band: number }) | null)[];
  value: boolean[];
  budget: number;
  cur: SlipStats;
  yours: {
    key: 'yours';
    title: string;
    blurb: string;
    stakes: number[];
    stats: SlipStats;
    likely: { mask: number; prob: number; net: number }[];
    exposure: string | null;
  };
  noSuggestions: string | null;
  checkedBudget: number;
  options: {
    key: SuggestionKey;
    title: string;
    blurb: string;
    stakes: number[];
    stats: SlipStats;
    likely: { mask: number; prob: number; net: number }[];
    exposure: string | null;
  }[];
  withStakes: (st: number[]) => PricedBet[];
  pairs: { a: number; b: number; r: number }[];
  likely: { mask: number; prob: number; net: number }[];
  /** Exposure group per bet (bets that win and lose together share one), and the bets suggestions may use. */
  groups: number[];
  usable: boolean[];
  /** This model's simulated outcomes (which bets hit, how often). */
  outcomes: SlipOutcome[];
};

const VERDICT = {
  value: { label: 'Value', color: 'success' as const, variant: 'filled' as const },
  close: { label: 'Too close', color: 'default' as const, variant: 'outlined' as const },
  drop: { label: 'Overpriced', color: 'error' as const, variant: 'filled' as const },
};
// A negative edge inside the margin of error is as uncertain as a small positive one, so it's "too close" too; only
// an edge below minus the margin is overpriced.
const verdictOf = (e: Analysis['evals'][number]) =>
  !e
    ? null
    : e.confidence === 'clear' || e.confidence === 'marginal'
      ? 'value'
      : e.confidence === 'within' || e.edgePts >= -e.band
        ? 'close'
        : 'drop';

/**
 * Overview when comparing models: each bet's chance and verdict under each model (and where they disagree), then the
 * slip's numbers and profit or loss under each. Suggestions are per model (switch to one), or use Risk and Goals,
 * which can judge stakes under both.
 */
const CompareOverview: React.FC<{ labels: string[]; bets: BetDraft[]; a: Analysis; b: Analysis }> = ({
  labels,
  bets,
  a,
  b,
}) => {
  const c = useModelColors();
  const k = useChartTokens();
  const { ref, width } = useWidth(280, 760);
  const models = [
    { name: 'Base', short: 'Base', an: a, color: c.base },
    { name: 'Track Specialists', short: 'Track Specialists', an: b, color: c.layered },
  ];
  // Each model gets three real columns (chance, edge, verdict), so every value lines up down the table whatever the
  // width of its neighbors. A rule on the left separates the two models' groups.
  const groupStart = { borderLeft: 1, borderColor: 'divider', pl: 2 };
  const cells = (an: Analysis, i: number) => {
    const e = an.evals[i];
    const v = verdictOf(e);
    return (
      <>
        <Box component="td" sx={groupStart}>
          <Typography variant="body2" sx={{ fontWeight: 700, fontVariantNumeric: 'tabular-nums' }}>
            {pct(an.probs[i])}
          </Typography>
        </Box>
        <td>
          {e && (
            <Typography variant="caption" color="text.secondary" sx={{ fontVariantNumeric: 'tabular-nums' }}>
              {e.edgePts > 0 ? '+' : e.edgePts < 0 ? '−' : ''}
              {Math.abs(e.edgePts).toFixed(1)} pts
            </Typography>
          )}
        </td>
        <Box component="td" sx={{ textAlign: 'left !important' }}>
          {v && <Chip size="small" {...VERDICT[v]} />}
        </Box>
      </>
    );
  };
  // The two models' profit or loss on the same scale, as lines.
  const H = 140;
  const edges = histogramEdges(a.outcomes.concat(b.outcomes), [a.priced], 24);
  const hists = models.map((m) => profitHistogram(m.an.outcomes, a.priced, edges));
  const max = Math.max(...hists.flat()) * 1.1 || 1;
  const n = edges.length - 1;
  const bw = (width - 20) / n;
  const x = (v: number) => 10 + ((v - edges[0]) / (edges[n] - edges[0])) * (width - 20);
  const step = (h: number[]) =>
    h.map((v, i) => `${10 + bw * i},${H - (v / max) * H} ${10 + bw * (i + 1)},${H - (v / max) * H}`).join(' ');
  const tiles: [string, (s: SlipStats) => string][] = [
    ['Expected profit', (s) => money(s.ev)],
    ['Chance of profit', (s) => pct(s.pProfit)],
    ['Typical result', (s) => money(s.p50)],
    ['Most likely range', (s) => `${money(s.p10)} to ${money(s.p90)}`],
  ];
  return (
    <>
      <Box sx={{ overflowX: 'auto' }}>
        <Box
          component="table"
          sx={{
            width: '100%',
            minWidth: 900,
            borderCollapse: 'collapse',
            '& td, & th': {
              py: 0.75,
              px: 1,
              borderBottom: 1,
              borderColor: 'divider',
              textAlign: 'right',
              whiteSpace: 'nowrap',
            },
            // Only the bet name wraps.
            '& td:first-of-type': { whiteSpace: 'normal' },
            '& th': { color: 'text.secondary', fontWeight: 500, fontSize: 13 },
            '& td:first-of-type, & th:first-of-type': { textAlign: 'left' },
          }}
        >
          <thead>
            <tr>
              <th>Bet</th>
              <th>Odds</th>
              <th>Stake</th>
              <th>Book implies</th>
              {models.map((m) => (
                <Box component="th" key={m.name} colSpan={3} sx={{ ...groupStart, textAlign: 'left !important' }}>
                  <Swatch c={m.color} />
                  {m.name}
                </Box>
              ))}
              <Box component="th" sx={groupStart}>
                Models
              </Box>
            </tr>
          </thead>
          <tbody>
            {bets.map((bet, i) => {
              const va = verdictOf(a.evals[i]);
              const vb = verdictOf(b.evals[i]);
              const agree = va === vb;
              return (
                <tr key={bet.id}>
                  <td>
                    <Typography variant="body2">{labels[i]}</Typography>
                  </td>
                  <td>
                    <Typography variant="body2">{fmtOdds(parseAmericanOdds(bet.oddsText))}</Typography>
                  </td>
                  <td>
                    <Typography variant="body2">{dollars(a.priced[i].stake)}</Typography>
                  </td>
                  <td>
                    <Typography variant="body2">{a.evals[i] ? pct(a.evals[i]!.implied) : '—'}</Typography>
                  </td>
                  {cells(a, i)}
                  {cells(b, i)}
                  <Box component="td" sx={groupStart}>
                    <Typography
                      variant="caption"
                      sx={{
                        fontWeight: 600,
                        color: !agree ? 'warning.main' : va === 'value' ? 'success.main' : 'text.secondary',
                      }}
                    >
                      {!va || !vb ? '' : !agree ? 'Disagree' : va === 'value' ? 'Value in both' : 'Agree'}
                    </Typography>
                  </Box>
                </tr>
              );
            })}
          </tbody>
        </Box>
      </Box>

      <Typography variant="h6" sx={{ mt: 3, mb: 1.5 }}>
        How this slip plays out under each model
      </Typography>
      {a.budget <= 0 ? (
        <Typography variant="body2" color="text.secondary">
          Add stakes to see how the slip plays out.
        </Typography>
      ) : (
        <>
          <Stack direction="row" spacing={1.5} flexWrap="wrap" useFlexGap sx={{ mb: 2 }}>
            {tiles.map(([label, f]) => (
              <Box
                key={label}
                sx={{ flex: '1 1 170px', p: 1.25, border: 1, borderColor: 'divider', borderRadius: 1.5 }}
              >
                <Typography variant="caption" color="text.secondary">
                  {label}
                </Typography>
                {models.map((m) => (
                  <Typography key={m.name} sx={{ fontWeight: 700, fontVariantNumeric: 'tabular-nums' }}>
                    <Swatch c={m.color} />
                    {f(m.an.cur)}
                  </Typography>
                ))}
              </Box>
            ))}
          </Stack>
          <Stack direction="row" spacing={2} sx={{ mb: 0.5 }}>
            {models.map((m) => (
              <Typography key={m.name} variant="caption">
                <Swatch c={m.color} />
                {m.name}
              </Typography>
            ))}
          </Stack>
          <Box ref={ref}>
            <svg
              width={width}
              height={H + 18}
              style={{ display: 'block' }}
              role="img"
              aria-label="Profit or loss by model"
            >
              <ProfitSides x0={x(edges[0])} xZero={x(0)} x1={x(edges[n])} H={H} />
              {hists.map((h, i) => (
                <polyline key={i} points={step(h)} fill="none" stroke={models[i].color} strokeWidth={2} />
              ))}
              <line x1={x(0)} x2={x(0)} y1={0} y2={H} stroke={k.axisText} />
              {[edges[0], 0, edges[n]].map((d, i) => (
                <text
                  key={i}
                  x={x(d)}
                  y={H + 14}
                  textAnchor={i === 0 ? 'start' : i === 2 ? 'end' : 'middle'}
                  fontSize={11}
                  fill={k.axisText}
                >
                  {d === 0 ? '$0' : money(d)}
                </text>
              ))}
            </svg>
          </Box>
          <Typography variant="caption" color="text.secondary">
            How often each profit or loss came up under each model (orange side = losing money, green side = making
            money).
          </Typography>
          <Typography variant="body2" color="text.secondary" sx={{ mt: 2 }}>
            Suggested slips are per model: switch to Base or Track Specialists to see them. Risk and Goals can judge
            stakes under both models.
          </Typography>
        </>
      )}
    </>
  );
};

const VerdictTable: React.FC<{ labels: string[]; bets: BetDraft[]; analysis: Analysis; missing: boolean[] }> = ({
  labels,
  bets,
  analysis,
  missing,
}) => (
  <Box sx={{ overflowX: 'auto' }}>
    <Box
      component="table"
      sx={{
        width: '100%',
        borderCollapse: 'collapse',
        '& td, & th': {
          py: 0.75,
          px: 1,
          borderBottom: 1,
          borderColor: 'divider',
          textAlign: 'right',
          whiteSpace: 'nowrap',
          fontSize: '0.875rem',
        },
        '& th': { color: 'text.secondary', fontWeight: 500 },
        '& td:first-of-type, & th:first-of-type': { textAlign: 'left', whiteSpace: 'normal', minWidth: 160 },
      }}
    >
      <thead>
        <tr>
          <th>Bet</th>
          <th>Odds</th>
          <th>Stake</th>
          <th>Our chance</th>
          <th>Book implies</th>
          <th>Edge</th>
          <th>Expected</th>
          <th />
        </tr>
      </thead>
      <tbody>
        {bets.map((b, i) => {
          const e = analysis.evals[i];
          const v = verdictOf(e);
          const odds = parseAmericanOdds(b.oddsText);
          return (
            <tr key={b.id}>
              <td>
                {labels[i]}
                {missing[i] && (
                  <Typography variant="caption" color="error" sx={{ display: 'block' }}>
                    A driver isn't in this field, so it can't hit.
                  </Typography>
                )}
              </td>
              <td>{fmtOdds(odds)}</td>
              <td>{dollars(analysis.priced[i].stake)}</td>
              <td>
                <b>{pct(analysis.probs[i])}</b>
              </td>
              <td>{odds != null ? pct(impliedProb(odds)) : '—'}</td>
              <td>
                {e ? `${e.edgePts > 0 ? '+' : ''}${e.edgePts.toFixed(1)} pts` : '—'}
                {e && (
                  <Typography component="span" variant="caption" color="text.secondary">
                    {' '}
                    ±{e.band.toFixed(1)}
                  </Typography>
                )}
              </td>
              <td>{e ? money((analysis.priced[i].stake * e.ev100) / 100) : '—'}</td>
              <td>
                {v && (
                  <Chip size="small" label={VERDICT[v].label} color={VERDICT[v].color} variant={VERDICT[v].variant} />
                )}
              </td>
            </tr>
          );
        })}
      </tbody>
    </Box>
  </Box>
);

const Suggestions: React.FC<{ labels: string[]; bets: BetDraft[]; analysis: Analysis }> = ({
  labels,
  bets,
  analysis,
}) => {
  // Collapsed by default: the slips and their likely results are the main event; these are per-bet details.
  const [open, setOpen] = useState(false);
  const items: React.ReactNode[] = [];
  bets.forEach((b, i) => {
    const e = analysis.evals[i];
    const v = verdictOf(e);
    if (!e || !v || v === 'value' || !analysis.priced[i].stake) return;
    const target = americanForProb(analysis.probs[i] - e.band / 100);
    const at = target != null ? `${fmtOdds(target)} or better` : 'no realistic price';
    items.push(
      v === 'drop' ? (
        <>
          <b>Drop {labels[i]}</b> at {b.oddsText}: the book wants {pct(e.implied)}, we have it at{' '}
          {pct(analysis.probs[i])}. Worth it at {at}.
        </>
      ) : (
        <>
          <b>{labels[i]}</b> at {b.oddsText} is too close to call (within our margin of error). Shop for {at}, or skip
          it.
        </>
      ),
    );
  });
  analysis.pairs.forEach(({ a, b, r }) =>
    items.push(
      r > 0 ? (
        <>
          <b>{labels[a]}</b> and <b>{labels[b]}</b> tend to win and lose together, so together they're riskier than two
          separate bets.
        </>
      ) : (
        <>
          <b>{labels[a]}</b> and <b>{labels[b]}</b> pull against each other: when one wins the other usually loses. That
          steadies the slip but caps the upside.
        </>
      ),
    ),
  );
  if (items.length === 0) return null;
  return (
    <>
      <Button
        size="small"
        color="inherit"
        onClick={() => setOpen((o) => !o)}
        aria-expanded={open}
        endIcon={<ExpandMore sx={{ transform: open ? 'rotate(180deg)' : 'none', transition: 'transform 0.15s' }} />}
        sx={{ mt: 2, px: 0, textTransform: 'none', fontWeight: 600 }}
      >
        Notes on each bet ({items.length})
      </Button>
      {open && (
        <Stack component="ul" spacing={0.75} sx={{ pl: 2.5, m: 0 }}>
          {items.map((it, i) => (
            <li key={i}>
              <Typography variant="body2">{it}</Typography>
            </li>
          ))}
        </Stack>
      )}
    </>
  );
};

const Tile: React.FC<{ label: string; value: string; sub?: string; strong?: boolean }> = ({
  label,
  value,
  sub,
  strong,
}) => (
  <Box sx={{ flex: '1 1 140px', p: 1.5, border: 1, borderColor: 'divider', borderRadius: 1.5 }}>
    <Typography variant="caption" color="text.secondary">
      {label}
    </Typography>
    <Typography
      variant="h6"
      sx={{ fontWeight: strong ? 700 : 600, fontVariantNumeric: 'tabular-nums', lineHeight: 1.3 }}
    >
      {value}
    </Typography>
    {sub && (
      <Typography variant="caption" color="text.secondary">
        {sub}
      </Typography>
    )}
  </Box>
);

const OptionCard: React.FC<{
  title: string;
  blurb: string;
  labels: string[];
  current: number[];
  stakes: number[];
  stats: SlipStats;
  /** This slip's most likely results (which bets hit and the profit). */
  likely: { mask: number; prob: number; net: number }[];
  /** The biggest single exposure, e.g. "64% rides on Kyle Larson (3 bets)" (shown at 40% or more). */
  exposure: string | null;
  /** Outlined: drawn on the chart. Viewing: these are the stakes the results above show. */
  active: boolean;
  viewing: boolean;
  onSelect: () => void;
  onUse: () => void;
}> = ({ title, blurb, labels, current, stakes, stats, likely, exposure, active, viewing, onSelect, onUse }) => (
  <Box
    onClick={onSelect}
    sx={{
      // Fixed width in the sideways-scrolling row: most of the screen on phones, 300px elsewhere.
      flex: { xs: '0 0 85%', sm: '0 0 300px' },
      minWidth: 0,
      scrollSnapAlign: 'start',
      p: 1.5,
      border: 2,
      borderColor: active ? 'primary.main' : 'divider',
      borderRadius: 1.5,
      cursor: 'pointer',
    }}
  >
    <Typography variant="subtitle1" sx={{ fontWeight: 600 }}>
      {title}
    </Typography>
    <Typography variant="caption" color="text.secondary">
      {blurb}
    </Typography>
    <Stack spacing={0.25} sx={{ my: 1 }}>
      {labels.map((l, i) =>
        !current[i] && !stakes[i] ? null : (
          <Stack
            key={i}
            direction="row"
            justifyContent="space-between"
            spacing={1}
            sx={{ opacity: stakes[i] ? 1 : 0.5 }}
          >
            <Typography
              variant="body2"
              sx={{ textDecoration: stakes[i] ? 'none' : 'line-through', minWidth: 0 }}
              noWrap
              title={l}
            >
              {l}
            </Typography>
            <Typography variant="body2" sx={{ fontVariantNumeric: 'tabular-nums', whiteSpace: 'nowrap' }}>
              {stakes[i] ? dollars(stakes[i]) : '—'}
              {stakes[i] !== current[i] && current[i] ? ` (yours ${dollars(current[i])})` : ''}
            </Typography>
          </Stack>
        ),
      )}
    </Stack>
    {stats.staked < current.reduce((t, v) => t + v, 0) && (
      <Typography variant="body2" color="text.secondary" sx={{ mb: 0.5 }}>
        Keep {dollars(current.reduce((t, v) => t + v, 0) - stats.staked)} unbet
      </Typography>
    )}
    <Typography variant="body2">
      Expected <b>{money(stats.ev)}</b> on {dollars(stats.staked)} · chance of profit <b>{pct(stats.pProfit)}</b>
    </Typography>
    <Typography variant="caption" color="text.secondary" sx={{ display: 'block' }}>
      Typical result {money(stats.p50)} · lose everything {pct(stats.pAllLose)}
    </Typography>
    {exposure && (
      <Typography variant="caption" sx={{ display: 'block', color: 'warning.main' }}>
        {exposure}
      </Typography>
    )}
    {stats.ev < 0 && title !== 'Your slip' && (
      <Typography variant="caption" color="warning.main" sx={{ display: 'block' }}>
        Still expects a loss, just a smaller one than your slip.
      </Typography>
    )}
    <Typography variant="caption" color="text.secondary" sx={{ display: 'block', mt: 1 }}>
      Most likely results
    </Typography>
    {likely.map((o) => {
      const hits = labels.filter((_, i) => o.mask & (1 << i));
      return (
        <Stack key={o.mask} direction="row" spacing={1} alignItems="baseline">
          <Typography variant="body2" sx={{ width: 36, flexShrink: 0, fontVariantNumeric: 'tabular-nums' }}>
            {pct(o.prob)}
          </Typography>
          <Typography
            variant="body2"
            color="text.secondary"
            noWrap
            sx={{ flex: 1, minWidth: 0 }}
            title={hits.join(' + ')}
          >
            {hits.length ? hits.join(' + ') : 'nothing hits'}
          </Typography>
          <Typography
            variant="body2"
            sx={{ fontWeight: 600, fontVariantNumeric: 'tabular-nums', whiteSpace: 'nowrap' }}
          >
            {money(o.net)}
          </Typography>
        </Stack>
      );
    })}
    <Button
      size="small"
      variant={viewing ? 'contained' : 'outlined'}
      disabled={viewing}
      sx={{ mt: 1, display: 'block' }}
      onClick={(e) => {
        e.stopPropagation();
        onUse();
      }}
    >
      {viewing ? 'Showing above' : 'Show this slip'}
    </Button>
  </Box>
);

// Skew-normal distribution fitted by matching mean, standard deviation and skewness (method of moments). The family
// can only lean so far (|skew| < ~0.995), so stronger lean is capped.
const erf = (x: number) => {
  // Abramowitz-Stegun 7.1.26, accurate to ~1e-7: plenty for drawing.
  const t = 1 / (1 + 0.3275911 * Math.abs(x));
  const y =
    1 -
    ((((1.061405429 * t - 1.453152027) * t + 1.421413741) * t - 0.284496736) * t + 0.254829592) * t * Math.exp(-x * x);
  return x >= 0 ? y : -y;
};
const skewNormalFit = (mean: number, sd: number, skew: number) => {
  const g = Math.max(-0.95, Math.min(0.95, skew));
  const a = Math.abs(g) ** (2 / 3);
  const delta = Math.sign(g) * Math.sqrt((Math.PI / 2) * (a / (a + ((4 - Math.PI) / 2) ** (2 / 3))));
  const omega = sd / Math.sqrt(1 - (2 * delta * delta) / Math.PI);
  return { xi: mean - omega * delta * Math.sqrt(2 / Math.PI), omega, alpha: delta / Math.sqrt(1 - delta * delta) };
};
const skewNormalPdf = (x: number, f: { xi: number; omega: number; alpha: number }) => {
  const z = (x - f.xi) / f.omega;
  const phi = Math.exp(-0.5 * z * z) / Math.sqrt(2 * Math.PI);
  const Phi = 0.5 * (1 + erf((f.alpha * z) / Math.SQRT2));
  return (2 / f.omega) * phi * Phi;
};

/** Profit/loss across the simulated races as bars or a fitted curve; click a bar to see what's in it. */
const PayoutChart: React.FC<{
  outcomes: SimulationSlip['outcomes'];
  base: PricedBet[];
  baseStats: SlipStats;
  /** Bars (share of races per profit range) or a smoothed curve of the same outcomes. */
  view: 'bars' | 'curve';
  /** What's inside a clicked bar: the outcomes (which bets hit) whose profit lands in it. */
  renderBin?: (
    rows: { mask: number; prob: number; net: number }[],
    lo: number,
    hi: number,
    share: number,
  ) => React.ReactNode;
}> = ({ outcomes, base, baseStats, view, renderBin }) => {
  const [selected, setSelected] = useState<number | null>(null);
  const clipId = React.useId().replace(/:/g, '');
  const k = useChartTokens();
  const theme = useTheme();
  const { ref, width } = useWidth(280, 640);
  const loss = k.dark ? '#d95926' : '#eb6834';
  const gain = theme.palette.primary.main;
  const edges = histogramEdges(outcomes, [base]);
  const bars = profitHistogram(outcomes, base, edges);
  const H = 240;
  const M = { l: 44, r: 12, t: 30, b: 34 };
  const n = bars.length;
  const pw = width - M.l - M.r;
  const bw = pw / n;
  // Curve view: a fitted distribution, not smoothed bars. Each slip gets a skew-normal curve matching its average,
  // spread and lean (how lopsided the outcomes are), so a slip that can only lose its stake but has longshot upside
  // shows a steep loss side and a long winning tail. Scaled to the bars' units so the two views line up.
  const binW = edges[1] - edges[0];
  const span = edges[edges.length - 1] - edges[0];
  const totalSims = outcomes.reduce((t, o) => t + o.sims, 0) || 1;
  const density = (bets: PricedBet[]) => {
    const pts = outcomes.map((o) => ({ v: netForMask(o.hitMask, bets), w: o.sims / totalSims }));
    const mean = pts.reduce((t, p) => t + p.v * p.w, 0);
    const sd = Math.sqrt(pts.reduce((t, p) => t + p.w * (p.v - mean) ** 2, 0)) || binW;
    const skew = pts.reduce((t, p) => t + p.w * ((p.v - mean) / sd) ** 3, 0);
    const fit = skewNormalFit(mean, sd, skew);
    return Array.from({ length: 161 }, (_, i) => {
      const x = edges[0] + (span * i) / 160;
      return { x, d: skewNormalPdf(x, fit) * binW };
    });
  };
  const curve = view === 'curve' ? density(base) : null;
  const ymax = (view === 'curve' ? Math.max(...curve!.map((p) => p.d)) : Math.max(...bars)) * 1.12 || 1;
  const y = (v: number) => M.t + (H - M.t - M.b) * (1 - v / ymax);
  const xv = (d: number) => M.l + ((d - edges[0]) / (edges[edges.length - 1] - edges[0])) * pw;
  const tickStep = ymax > 0.5 ? 0.2 : ymax > 0.25 ? 0.1 : 0.05;
  const yTicks = Array.from({ length: Math.floor(ymax / tickStep) + 1 }, (_, i) => i * tickStep);
  const xEvery = Math.max(1, Math.ceil(n / Math.max(3, Math.floor(pw / 70))));
  // The clicked bar (cleared if the bins change under it), and the outcomes inside it, binned as profitHistogram does.
  const sel = view === 'bars' && selected != null && selected < bars.length && bars[selected] > 0 ? selected : null;
  const binOf = (v: number) => {
    const i = edges.findIndex((e, k) => k < edges.length - 1 && v >= e && v < edges[k + 1]);
    return i < 0 ? (v < edges[0] ? 0 : edges.length - 2) : i;
  };
  // Outcomes that differ only in bets with no stake pay the same, so they're merged into one line.
  const stakedMask = base.reduce((m, b, i) => (b.stake > 0 ? m | (1 << i) : m), 0);
  const inBin =
    sel == null
      ? []
      : Array.from(
          outcomes
            .reduce((acc, o) => {
              const m = o.hitMask & stakedMask;
              acc.set(m, (acc.get(m) ?? 0) + o.sims);
              return acc;
            }, new Map<number, number>())
            .entries(),
        )
          .map(([mask, sims]) => ({ mask, prob: sims / totalSims, net: netForMask(mask, base) }))
          .filter((o) => binOf(o.net) === sel)
          .sort((a, b) => b.prob - a.prob);
  return (
    <Box ref={ref} sx={{ width: '100%' }}>
      <svg
        width={width}
        height={H}
        role="img"
        aria-label="Profit or loss across the simulated races"
        style={{ display: 'block', overflow: 'visible' }}
      >
        {yTicks.map((v) => (
          <g key={v}>
            <line x1={M.l} x2={width - M.r} y1={y(v)} y2={y(v)} stroke={k.grid} />
            {view === 'bars' && (
              <text x={M.l - 6} y={y(v)} dy="0.32em" textAnchor="end" fontSize={11} fill={k.axisText}>
                {pct(v)}
              </text>
            )}
          </g>
        ))}
        {view === 'bars' ? (
          <>
            {bars.map((v, i) =>
              v > 0 ? (
                <g
                  key={i}
                  style={{ cursor: renderBin ? 'pointer' : undefined }}
                  onClick={() => setSelected(sel === i ? null : i)}
                >
                  {/* Full-height hit area, easier to click than a short bar. */}
                  <rect x={M.l + bw * i} width={bw} y={M.t} height={y(0) - M.t} fill="transparent" />
                  <rect
                    x={M.l + bw * i + Math.min(1, bw * 0.1)}
                    width={Math.max(1, bw - Math.min(2, bw * 0.2))}
                    y={y(v)}
                    height={y(0) - y(v)}
                    rx={2}
                    fill={edges[i] < 0 ? loss : gain}
                    fillOpacity={sel == null || sel === i ? 0.85 : 0.4}
                    stroke={sel === i ? k.text : 'none'}
                    strokeWidth={2}
                  />
                </g>
              ) : null,
            )}
          </>
        ) : (
          <>
            {/* Losses left of break even in orange, gains right of it in green. */}
            <defs>
              <clipPath id={`${clipId}-loss`}>
                <rect x={M.l} y={0} width={Math.max(0, xv(0) - M.l)} height={H} />
              </clipPath>
              <clipPath id={`${clipId}-range`}>
                <rect
                  x={xv(baseStats.p10)}
                  y={0}
                  width={Math.max(0, xv(baseStats.p90) - xv(baseStats.p10))}
                  height={H}
                />
              </clipPath>
              <clipPath id={`${clipId}-gain`}>
                <rect x={xv(0)} y={0} width={Math.max(0, width - M.r - xv(0))} height={H} />
              </clipPath>
            </defs>
            {[
              { id: 'loss', c: loss },
              { id: 'gain', c: gain },
            ].map((side) => {
              const line = curve!.map((p, i) => `${i ? 'L' : 'M'}${xv(p.x)},${y(p.d)}`).join(' ');
              return (
                <g key={side.id} clipPath={`url(#${clipId}-${side.id})`}>
                  {/* Light fill everywhere, stronger inside the 8-in-10 range. */}
                  <path
                    d={`${line} L${xv(edges[edges.length - 1])},${y(0)} L${xv(edges[0])},${y(0)} Z`}
                    fill={side.c}
                    fillOpacity={0.12}
                  />
                  <g clipPath={`url(#${clipId}-range)`}>
                    <path
                      d={`${line} L${xv(edges[edges.length - 1])},${y(0)} L${xv(edges[0])},${y(0)} Z`}
                      fill={side.c}
                      fillOpacity={0.34}
                    />
                  </g>
                  <path d={line} fill="none" stroke={side.c} strokeWidth={2} strokeLinejoin="round" />
                </g>
              );
            })}
          </>
        )}
        <line x1={xv(0)} x2={xv(0)} y1={M.t - 14} y2={H - M.b} stroke={k.axisText} strokeWidth={1.5} />
        <text x={xv(0) + 4} y={M.t - 18} fontSize={11} fill={k.axisText}>
          break even
        </text>
        <line x1={xv(baseStats.ev)} x2={xv(baseStats.ev)} y1={M.t} y2={H - M.b} stroke={k.text} strokeDasharray="2 3" />
        <text x={xv(baseStats.ev) + 4} y={M.t + 10} fontSize={11} fill={k.text}>
          expected {money(baseStats.ev)}
        </text>
        {edges.map((d, i) =>
          i % xEvery === 0 ? (
            <text key={i} x={xv(d)} y={H - 16} textAnchor="middle" fontSize={11} fill={k.axisText}>
              {d === 0 ? '$0' : money(d)}
            </text>
          ) : null,
        )}
        <text x={M.l + pw / 2} y={H - 1} textAnchor="middle" fontSize={11} fill={k.axisText}>
          profit or loss on the whole slip
        </text>
        {view === 'curve' && (
          <text
            transform={`translate(14 ${M.t + (H - M.t - M.b) / 2}) rotate(-90)`}
            textAnchor="middle"
            fontSize={11}
            fill={k.axisText}
          >
            more likely ↑
          </text>
        )}
      </svg>
      {sel != null && renderBin && (
        <Box sx={{ mt: 1, p: 1.5, border: 1, borderColor: 'divider', borderRadius: 1.5 }}>
          {renderBin(inBin, edges[sel], edges[sel + 1], bars[sel])}
        </Box>
      )}
    </Box>
  );
};
